// pages/Dashboard/Dashboard.js
import React, { useEffect } from "react";
import ChatbotScripts from './ChatbotScripts';
import UsedTokensChart from "./UsedTokenByDate";
import LlmCallLogCharts from "./LlmCallLogCharts";
import StatsBox from "./StatsBox";
import ChatbotStatsTable from "./StatsTable";
import { useChatbots } from "../../../contexts/ChatbotContext";
import { Box, CircularProgress, Typography, Grid } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import BoltIcon from '@mui/icons-material/Bolt';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#3b82f6',
};

export default function Dashboard() {
    const { activeChatbot, isLoading: isChatbotLoading } = useChatbots();

    useEffect(() => {
        document.title = 'Dashboard | KlonIT Web Application';
    }, []);

    if (isChatbotLoading) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh',
                gap: 2,
            }}>
                <CircularProgress size={40} sx={{ color: colors.accent }} />
                <Typography sx={{ fontFamily, fontSize: '0.9rem', color: colors.textSecondary }}>
                    Loading dashboard...
                </Typography>
            </Box>
        );
    }

    if (!activeChatbot) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '60vh',
                gap: 2,
            }}>
                <SmartToyOutlinedIcon sx={{ fontSize: 48, color: colors.textTertiary }} />
                <Typography sx={{ fontFamily, fontSize: '1rem', color: colors.textSecondary }}>
                    No chatbot selected. Please create or select a chatbot.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ fontFamily, maxWidth: '1200px', width: '100%', mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Box sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                    boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
                }}>
                    <DashboardIcon sx={{ color: '#fff', fontSize: 28 }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                    <Typography sx={{
                        fontFamily,
                        fontSize: '1.5rem',
                        fontWeight: 700,
                        color: colors.textPrimary,
                        letterSpacing: '-0.02em',
                    }}>
                        Dashboard
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        bgcolor: 'rgba(59,130,246,0.1)',
                        px: 2,
                        py: 0.5,
                        borderRadius: '20px',
                    }}>
                        <SmartToyOutlinedIcon sx={{ fontSize: 16, color: colors.accent }} />
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: colors.accent }}>
                            {activeChatbot.chatbot_name}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 1 }}>
                    <BoltIcon sx={{ fontSize: 14, color: colors.textTertiary }} />
                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: colors.textTertiary }}>
                        Analytics & Statistics
                    </Typography>
                </Box>
            </Box>

            {/* Stats Summary Boxes */}
            <Box sx={{
                bgcolor: 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(12px)',
                borderRadius: '16px',
                p: 3,
                mb: 3,
                border: '1px solid rgba(0,0,0,0.06)',
            }}>
                <StatsBox />
            </Box>

            {/* Scripts and Stats Table */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} lg={6}>
                    <Box sx={{
                        bgcolor: 'rgba(255,255,255,0.7)',
                        backdropFilter: 'blur(12px)',
                        borderRadius: '16px',
                        p: 3,
                        border: '1px solid rgba(0,0,0,0.06)',
                        height: '100%',
                    }}>
                        <ChatbotScripts />
                    </Box>
                </Grid>
                <Grid item xs={12} lg={6}>
                    <Box sx={{
                        bgcolor: 'rgba(255,255,255,0.7)',
                        backdropFilter: 'blur(12px)',
                        borderRadius: '16px',
                        p: 3,
                        border: '1px solid rgba(0,0,0,0.06)',
                        height: '100%',
                    }}>
                        <ChatbotStatsTable />
                    </Box>
                </Grid>
            </Grid>

            {/* Token Usage Chart */}
            <Box sx={{
                bgcolor: 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(12px)',
                borderRadius: '16px',
                p: 3,
                border: '1px solid rgba(0,0,0,0.06)',
            }}>
                <UsedTokensChart />
            </Box>

            {/* Token Usage Chart */}
            <Box sx={{
                bgcolor: 'rgba(255,255,255,0.7)',
                backdropFilter: 'blur(12px)',
                borderRadius: '16px',
                p: 3,
                border: '1px solid rgba(0,0,0,0.06)',
            }}>
                <LlmCallLogCharts />
            </Box>
        </Box>
    );
}
